import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { 
  FaHeart, FaLightbulb, FaHandshake, FaRocket, FaBalanceScale, 
  FaMicroscope, FaGlobeAmericas, FaRainbow, FaVenusMars, FaUniversalAccess,
  FaHandHoldingHeart, FaGraduationCap, FaLeaf, FaUsers, FaBrain,
  FaHandsHelping, FaTheaterMasks, FaFilm, FaPeopleCarry, FaQuoteLeft,
  FaChartLine, FaShieldAlt, FaRobot, FaDatabase, FaFingerprint, FaCogs,
  FaCloudUploadAlt, FaLock, FaUserShield, FaNetworkWired, FaProjectDiagram, FaCodeBranch, FaLayerGroup, FaSyncAlt, FaServer, FaMicrochip, FaFlask
} from 'react-icons/fa';
import '../styles/OurStory.css';

const OurStory = () => {
  const values = [
    { 
      icon: <FaHeart />, 
      title: 'Passion for Impact', 
      description: 'We believe every great cause starts with someone brave enough to challenge the status quo and make a difference.' 
    },
    { 
      icon: <FaLightbulb />, 
      title: 'Innovation First', 
      description: "We combine cutting-edge technology with proven nonprofit practices to accelerate social change and maximize impact." 
    },
    { 
      icon: <FaHandshake />, 
      title: 'True Partnership', 
      description: "Your mission is our mission. We provide hands-on support, guidance, and expertise every step of the way." 
    },
    { 
      icon: <FaRocket />, 
      title: 'Launch Pad', 
      description: 'We provide the infrastructure, tools, and expertise to turn your vision into measurable, sustainable impact.' 
    }
  ];

  const principles = [
    { 
      icon: <FaMicroscope />, 
      title: 'Champions of Science', 
      description: 'We advocate for evidence-based solutions and scientific advancement. From medical research to climate science, we support initiatives that push the boundaries of human knowledge.' 
    },
    { 
      icon: <FaRainbow />, 
      title: 'LGBTQ+ Advocacy', 
      description: 'We proudly support LGBTQ+ rights and initiatives. Equality, inclusion, and human dignity are non-negotiable values in our community.' 
    },
    { 
      icon: <FaVenusMars />, 
      title: 'Gender Equality', 
      description: "We champion women's rights and gender equality. We support initiatives that break down barriers and create opportunities for all." 
    },
    { 
      icon: <FaUniversalAccess />, 
      title: 'Accessibility & Inclusion', 
      description: "We believe in creating a world that's accessible to everyone. Disability rights and universal design are core to our mission." 
    }
  ];

  const supportAreas = [
    { 
      icon: <FaBalanceScale />,
      title: 'Social Justice', 
      examples: [
        'Civil Rights & Advocacy',
        'Immigration Reform',
        'Economic Equality',
        'Criminal Justice Reform'
      ]
    },
    { 
      icon: <FaGraduationCap />,
      title: 'Education', 
      examples: [
        'STEM Programs',
        'Arts Education',
        'Adult Learning',
        'Educational Equity'
      ]
    },
    { 
      icon: <FaBrain />,
      title: 'Health & Research', 
      examples: [
        'Mental Health',
        'Medical Research',
        'Public Health',
        'Healthcare Access'
      ]
    },
    { 
      icon: <FaLeaf />,
      title: 'Environment', 
      examples: [
        'Climate Action',
        'Conservation',
        'Sustainable Energy',
        'Environmental Justice'
      ]
    },
    { 
      icon: <FaTheaterMasks />,
      title: 'Arts & Culture', 
      examples: [
        'Documentary Film',
        'Performing Arts',
        'Cultural Preservation',
        'Community Arts'
      ]
    },
    { 
      icon: <FaPeopleCarry />,
      title: 'Community Development', 
      examples: [
        'Affordable Housing',
        'Food Security',
        'Youth Programs',
        'Community Centers'
      ]
    }
  ];

  const milestones = [
    {
      year: "2024",
      title: "Foundation",
      description: "Established Cause & Impact to bridge the gap between passionate change-makers and their ability to create immediate impact."
    },
    {
      year: "2024",
      title: "Innovating for Impact",
      description: "Laid the groundwork for groundbreaking technology designed to streamline fiscal sponsorship operations and empower projects with tools to scale and thrive."
    },
    {
      year: "2024",
      title: "Launch",
      description: "Opened our doors to visionary individuals and groups ready to make their mark on the world."
    },
    {
      year: "2025",
      title: "Expanding Impact",
      description: "Goal: Enable 1000+ Causes to create measurable social change across education, healthcare, and environmental initiatives."
    },
    {
      year: "2025",
      title: "Technology Innovation",
      description: "Goal: Launch advanced impact tracking and donor-transparancy tools to help Causes measure and amplify their social return on investment."
    },
    {
      year: "2025",
      title: "Technology Innovation",
      description: "Cause and Impact introduces 'Just Ask Phil' — the first AI system engineered to redefine nonprofit and fiscal sponsorship compliance. Not just intelligent, but operational. Phil delivers real-time guidance, client interaction, and built-in deliverables — with the brainpower, precision, and productivity of an entire expert team. A new era, powered by one Phil."
    },
    {
      year: "2025",
      title: "Community Growth",
      description: "Goal: Build a vibrant network of change-makers, fostering collaboration and knowledge sharing across Causes, Fiscal Sponsors, and other nonprofits."
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.value-card, .principle-card, .support-card')
      .forEach(card => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <title>Our Story | Cause & Impact</title>
        <meta 
          name="description" 
          content="Discover the heart behind Cause & Impact - where passion meets purpose, and every dream of positive change finds its wings." 
        />
      </Helmet>

      <div className="story-wrapper">
        <section className="story-hero">
          <div className="hero-content">
            <h1>Our Story</h1>
            <p className="hero-text main">
              Every great cause begins with a spark—an idea, a belief, a hope that the world can be better.
            </p>
            <p className="hero-text">
              At Cause & Impact, we fan that spark into a movement. We've reimagined what it means to 
              start and grow a nonprofit, removing the barriers that hold changemakers back.
            </p>
            <p className="hero-text">
              Through innovative fiscal sponsorship and our all-in-one support, we handle the 
              complexities—compliance, operations, infrastructure—so you can focus on what truly 
              matters: creating impact.
            </p>
            <p className="hero-text emphasis">
              Because your cause deserves more than just a chance; it deserves to thrive.
            </p>
            <p className="hero-text tagline">
              Your vision. Your impact. Our support. 
              <strong>Let's change the world, together.</strong>
            </p>
          </div>
        </section>
        <div className="section-divider"></div>

        <section className="story-section mission-section">
          <div className="content-container">
            <h2>Who We Are</h2>
            <div className="mission-content">
              <p className="mission-text main">
                Setting new standards in fiscal sponsorship.
              </p>
              <p className="mission-text">
                We combine deep industry expertise with innovative operational frameworks 
                to advance the entire fiscal sponsorship sector.
              </p>
              <p className="mission-text highlight">
                From emerging causes to established sponsors, we're driving excellence 
                across the nonprofit ecosystem.
              </p>
              <p className="mission-text emphasis">
                Through pioneering best practices and next-generation solutions, we're 
                shaping the future of fiscal sponsorship and nonprofit operations.
              </p>
              <div className="mission-pillars">
                <div className="pillar">
                  <FaBalanceScale className="pillar-icon" />
                  <span>Industry Excellence</span>
                </div>
                <div className="pillar">
                  <FaShieldAlt className="pillar-icon" />
                  <span>Operational Mastery</span>
                </div>
                <div className="pillar">
                  <FaChartLine className="pillar-icon" />
                  <span>Sector Innovation</span>
                </div>
              </div>
              <p className="mission-text emphasis">
                From grassroots movements to groundbreaking research, we're here to 
                amplify Causes to create lasting impact.
              </p>
            </div>
          </div>
        </section>
        <div className="section-divider"></div>

        <section className="story-section values-section">
          <div className="content-container">
            <h2>What Drives Us</h2>
            <div className="values-intro">
              <p>
                Behind every cause, there's a belief in a better tomorrow. 
                Here's what drives us, day after day, to champion change and 
                empower others to do the same.
              </p>
            </div>
            <div className="values-grid">
              {values.map((value, index) => (
                <div className="value-card" key={index}>
                  <div className="value-icon">{value.icon}</div>
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        <div className="section-divider"></div>

        <section className="story-section principles-section">
          <div className="content-container">
            <h2>What We Stand For</h2>
            <p className="section-intro">
              We're proud to be bold about our values. These aren't just principles – 
              they're commitments to the change we want to see in the world.
            </p>
            <div className="principles-grid">
              {principles.map((principle, index) => (
                <div className="principle-card" key={index}>
                  <div className="principle-icon">{principle.icon}</div>
                  <h3>{principle.title}</h3>
                  <p>{principle.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        <div className="section-divider"></div>

        <section className="story-section impact-stats">
          <div className="content-container">
            <h2>Impact in Motion</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon"><FaRocket /></div>
                <div className="stat-number">95%</div>
                <div className="stat-label">Of your Donation Goes to Your Cause, 5% Powers the Infrastructure to Make it Possible</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon"><FaHandshake /></div>
                <div className="stat-number">24/7</div>
                <div className="stat-label">Support for Your Mission</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon"><FaLightbulb /></div>
                <div className="stat-number">∞</div>
                <div className="stat-label">Possibilities for Change</div>
              </div>
            </div>
          </div>
        </section>
        <div className="section-divider"></div>

        <section className="story-section support-section">
          <div className="content-container">
            <h2>The Causes We Champion</h2>
            <p className="section-intro">
              From documentary filmmakers to medical researchers, community organizers 
              to environmental advocates – if you're working to make the world better, 
              you've found your home.
            </p>
            <div className="support-grid">
              {supportAreas.map((area, index) => (
                <div className="support-card" key={index}>
                  <div className="support-icon">{area.icon}</div>
                  <h3>{area.title}</h3>
                  <ul>
                    {area.examples.map((example, i) => (
                      <li key={i}>{example}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
        <div className="section-divider"></div>

        <section className="story-section timeline-section">
          <div className="content-container">
            <h2>Our Journey</h2>
            <div className="timeline">
              {milestones.map((milestone, index) => (
                <div className="milestone" key={index}>
                  <div className="milestone-year">
                    <span>{milestone.year}</span>
                  </div>
                  <div className="milestone-content">
                    <h3>{milestone.title}</h3>
                    <p>{milestone.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        <div className="section-divider"></div>

        <section className="story-section tech-innovation">
          <div className="content-container">
            <h2>Technology & Innovation</h2>
            <p className="section-intro">
              We're revolutionizing fiscal sponsorship through cutting-edge technology 
              and innovative solutions that make creating impact easier than ever.
            </p>
            <div className="tech-grid">
              <div className="tech-card">
                <div className="tech-icon">
                  <FaBrain size={40} style={{ color: '#fff' }} />
                </div>
                <h3>Smart Platform Integration</h3>
                <p>
                  AI-powered analytics delivering real-time insights into your cause's 
                  financial health, donor patterns, and program impact through intelligent 
                  data processing.
                </p>
              </div>
              <div className="tech-card">
                <div className="tech-icon">
                  <FaLayerGroup size={40} style={{ color: '#fff' }} />
                </div>
                <h3>Next-Gen Infrastructure</h3>
                <p>
                  Machine learning-enhanced fund accounting with smart bookkeeping automation 
                  and blockchain-level transparent audit trails for nonprofit operations.
                </p>
              </div>
              <div className="tech-card">
                <div className="tech-icon">
                  <FaMicrochip size={40} style={{ color: '#fff' }} />
                </div>
                <h3>Automated Intelligence</h3>
                <p>
                  Smart compliance monitoring with predictive analytics, intelligent expense 
                  tracking, and automated donor management powered by advanced algorithms.
                </p>
              </div>
            </div>
          </div>
        </section>
        <div className="section-divider"></div>

        <section className="story-section future-section">
          <div className="content-container">
            <h2>Building the Future</h2>
            <div className="future-content">
              <div className="future-text">
                <p>
                  We're at the beginning of an exciting journey. Our vision extends beyond 
                  traditional fiscal sponsorship – we're building a complete ecosystem for 
                  social impact.
                </p>
              </div>
              <div className="future-goals-grid">
                <div className="future-goal-card">
                  <div className="goal-icon">
                    <FaNetworkWired />
                  </div>
                  <h3>Cause Network</h3>
                  <p>
                    Building a collaborative network where causes can share resources, 
                    knowledge, and amplify each other's impact.
                  </p>
                </div>
                <div className="future-goal-card">
                  <div className="goal-icon">
                    <FaGraduationCap />
                  </div>
                  <h3>Impact Academy Growth</h3>
                  <p>
                    Expanding our educational resources to provide comprehensive training 
                    for nonprofit leaders and change-makers.
                  </p>
                </div>
                <div className="future-goal-card">
                  <div className="goal-icon">
                    <FaGlobeAmericas />
                  </div>
                  <h3>Global Reach</h3>
                  <p>
                    Scaling our platform to support causes worldwide, creating a global 
                    community of impact.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        <div className="section-divider"></div>

        <section className="story-section future-voices">
          <div className="content-container">
            <h2>Future Voices of Change</h2>
            <p className="section-intro">
              These spaces await the incredible stories of change-makers like you. 
              Join us in writing the next chapter of social impact.
            </p>
            <div className="testimonial-grid">
              <div className="testimonial-placeholder">
                <div className="placeholder-icon">
                  <FaQuoteLeft />
                </div>
                <p className="placeholder-text">
                  "Your story of impact could be here. Whether you're launching an 
                  educational program, advancing medical research, or protecting our 
                  environment – this space is ready for your vision."
                </p>
                <a href="/apply" className="placeholder-cta">Ready to make your mark?</a>
              </div>
              <div className="testimonial-placeholder">
                <div className="placeholder-icon">
                  <FaQuoteLeft />
                </div>
                <p className="placeholder-text">
                  "Imagine your cause being highlighted here, inspiring others and 
                  creating ripples of change throughout communities."
                </p>
                <a href="/apply" className="placeholder-cta">Your impact starts here</a>
              </div>
              <div className="testimonial-placeholder">
                <div className="placeholder-icon">
                  <FaQuoteLeft />
                </div>
                <p className="placeholder-text">
                  "This could be your success story – turning passion into impact, 
                  vision into reality, and dreams into measurable change."
                </p>
                <a href="/apply" className="placeholder-cta">Begin your journey</a>
              </div>
            </div>
          </div>
        </section>
        <div className="section-divider"></div>

        <section className="story-section join-section">
          <div className="content-container">
            <h2>Join the Movement</h2>
            <div className="join-content">
              <p className="join-text">
                Ready to turn your passion into impact? Whether you're fighting for 
                climate justice, advancing medical research, or bringing arts to your 
                community – we're here to help you succeed.
              </p>
              <div className="cta-buttons">
                <a href="/apply" className="cta-button primary">Start Your Journey</a>
                <a href="/services" className="cta-button secondary">Our Services</a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default OurStory; 